import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, type ReactNode, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { StatCard } from '@/components/ui/stat-card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { api } from '@/lib/api';
import { useFilters } from '@/lib/filters';
import { formatIsoDate, formatMetric } from '@/lib/format';
import { useViewMessages } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/*
 * Quiz — session-based tracking of /pages/treningskalkulator. Every visitor is
 * recorded (no fbclid gate, unlike visitor_attribution), but only step numbers
 * and question ids — never answers (those live exclusively on the Momence lead
 * the visitor consented to). Built for the ads-vs-LP comparison: rates first,
 * then three calm sections — funnel, anonymous quitter tallies (collapsed),
 * and the session list with the actual person behind each converted session.
 */

const MESSAGES = {
  nb: {
    title: 'Quiz',
    sub: (from: string, to: string) => `Perioden ${from} → ${to} — styres av datovelgeren øverst.`,
    statSessions: 'Økter',
    statSessionsSub: 'landet på quizen',
    statOpenRate: 'Åpningsrate',
    statOpenRateSub: (n: string) => `${n} startet`,
    statCompletionRate: 'Fullføringsrate',
    statCompletionRateSub: (n: string) => `${n} så profilen sin`,
    statCtaRate: 'CTA-rate',
    statCtaRateSub: (n: string) => `${n} trykket book`,
    statLeadRate: 'Lead-rate',
    statLeadRateSub: (n: string) => `${n} leads`,
    funnelTitle: 'Trakten',
    funnelSub: 'Hvor mange når hvert steg — fra landing til resultat.',
    landed: 'Landet',
    answersTitle: 'Hvem faller fra?',
    answersSub:
      'Anonyme dagstellere — aldri koblet til enkeltpersoner. Falt fra = alle minus fullførte.',
    colAll: 'Alle',
    colCompleted: 'Fullførte',
    colDropped: 'Falt fra',
    sessionsTitle: 'Siste økter',
    sessionsSub: 'Nyeste først — utvid en rad for reisen og personen bak.',
    colTime: 'Tidspunkt',
    colDevice: 'Enhet',
    colReached: 'Kom til',
    colStatus: 'Status',
    expandRow: 'Utvid rad',
    stepWord: 'steg',
    stepFallback: (n: number) => `Steg ${n}`,
    statusLead: 'Lead',
    statusCta: 'CTA',
    statusResult: 'Resultat',
    statusFell: (label: string) => `Falt av på ${label}`,
    adDotTitle: 'Annonse-klikk',
    callbackTitle: 'Ba om å bli oppringt',
    journeyTitle: 'Reisen',
    personTitle: 'Personen bak',
    personInMomence: 'I Momence som lead ✓',
    personNotForwarded: 'Lagret hos oss — Momence avviste (manuell oppfølging)',
    deviceMobile: 'Mobil',
    deviceDesktop: 'Desktop',
    deviceUnknown: 'Ukjent',
    events: {
      land: 'Landet',
      step: 'Steg',
      cta_click: 'CTA-klikk',
      lead_sent: 'Lead sendt',
      callback_request: 'Ba om oppringing',
      restart: 'Omstart',
    },
    /** Step names by question id (spec'd; 'Bookinger' sets the hardcoded-label precedent). */
    qidLabels: {
      intro: 'Intro',
      name: 'Fornavn',
      alder: 'Alder',
      mirror: 'Situasjon',
      goals: 'Mål',
      obstacle: 'Hinder',
      body: 'Kroppen',
      doctor: 'Lege',
      readiness: 'Når',
      contact: 'Kontakt',
      computing: 'Beregner',
      result: 'Resultat',
    } as Record<string, string>,
    /** Answer names by question id + answer id (fallback = raw id). */
    answerLabels: {
      alder: {
        u50: 'Under 50 år',
        '50-59': '50–59 år',
        '60-69': '60–69 år',
        '70pluss': '70+ år',
      },
      mirror: {
        utrent: 'Utrent / lenge siden',
        aldri: 'Aldri trent på senter',
        vondt: 'Har vondt',
        oppbygg: 'Etter operasjon/sykdom',
        frisk: 'I grei form',
      },
      obstacle: {
        redd: 'Redd for å ta i for hardt',
        passer: 'Usikker på om det passer',
        vetikke: 'Vet ikke hvor jeg skal starte',
        tid: 'Tiden strekker ikke til',
      },
      doctor: {
        ja: 'Lege anbefalt',
        vurderer: 'Tenkt tanken selv',
        nei: 'Fant hit selv',
      },
      readiness: {
        naa: 'Klar nå',
        snart: 'Snart',
        utforsker: 'Utforsker',
      },
      body: {
        rygg: 'Rygg',
        knar: 'Knær',
        hofte: 'Hofte',
        nyledd: 'Nytt ledd',
        artrose: 'Artrose',
        fibro: 'Fibromyalgi',
        nevro: 'Nevrologisk/ME',
        hjerte: 'Hjerte',
        etterop: 'Etter operasjon',
        ettersyk: 'Etter sykdom',
        ingen: 'Ingen av disse',
      },
    } as Record<string, Record<string, string>>,
    emptyTitle: 'Ingen quiz-økter i perioden',
    emptyHint:
      'Økter dukker opp her når quiz-beaconet er live på draft-temaet og noen åpner treningskalkulatoren.',
  },
  en: {
    title: 'Quiz',
    sub: (from: string, to: string) =>
      `Period ${from} → ${to} — controlled by the date picker at the top.`,
    statSessions: 'Sessions',
    statSessionsSub: 'landed on the quiz',
    statOpenRate: 'Open rate',
    statOpenRateSub: (n: string) => `${n} started`,
    statCompletionRate: 'Completion rate',
    statCompletionRateSub: (n: string) => `${n} saw their profile`,
    statCtaRate: 'CTA rate',
    statCtaRateSub: (n: string) => `${n} clicked book`,
    statLeadRate: 'Lead rate',
    statLeadRateSub: (n: string) => `${n} leads`,
    funnelTitle: 'The funnel',
    funnelSub: 'How many reach each step — from landing to result.',
    landed: 'Landed',
    answersTitle: 'Who drops off?',
    answersSub:
      'Anonymous day-level tallies — never linked to individuals. Dropped = all minus completed.',
    colAll: 'All',
    colCompleted: 'Completed',
    colDropped: 'Dropped',
    sessionsTitle: 'Recent sessions',
    sessionsSub: 'Newest first — expand a row for the journey and the person behind it.',
    colTime: 'Time',
    colDevice: 'Device',
    colReached: 'Reached',
    colStatus: 'Status',
    expandRow: 'Expand row',
    stepWord: 'step',
    stepFallback: (n: number) => `Step ${n}`,
    statusLead: 'Lead',
    statusCta: 'CTA',
    statusResult: 'Result',
    statusFell: (label: string) => `Dropped at ${label}`,
    adDotTitle: 'Ad click',
    callbackTitle: 'Requested a call',
    journeyTitle: 'Journey',
    personTitle: 'The person behind',
    personInMomence: 'In Momence as a lead ✓',
    personNotForwarded: 'Stored with us — Momence rejected (manual follow-up)',
    deviceMobile: 'Mobile',
    deviceDesktop: 'Desktop',
    deviceUnknown: 'Unknown',
    events: {
      land: 'Landed',
      step: 'Step',
      cta_click: 'CTA click',
      lead_sent: 'Lead sent',
      callback_request: 'Requested a call',
      restart: 'Restart',
    },
    qidLabels: {
      intro: 'Intro',
      name: 'First name',
      alder: 'Age',
      mirror: 'Situation',
      goals: 'Goals',
      obstacle: 'Obstacle',
      body: 'The body',
      doctor: 'Doctor',
      readiness: 'When',
      contact: 'Contact',
      computing: 'Calculating',
      result: 'Result',
    } as Record<string, string>,
    answerLabels: {
      alder: {
        u50: 'Under 50',
        '50-59': '50–59',
        '60-69': '60–69',
        '70pluss': '70+',
      },
      mirror: {
        utrent: 'Out of shape / been a while',
        aldri: 'Never trained at a gym',
        vondt: 'In pain',
        oppbygg: 'After surgery/illness',
        frisk: 'In decent shape',
      },
      obstacle: {
        redd: 'Afraid of overdoing it',
        passer: 'Unsure whether it suits me',
        vetikke: "Don't know where to start",
        tid: 'Not enough time',
      },
      doctor: {
        ja: 'Doctor recommended',
        vurderer: 'Considered it myself',
        nei: 'Found this on my own',
      },
      readiness: {
        naa: 'Ready now',
        snart: 'Soon',
        utforsker: 'Exploring',
      },
      body: {
        rygg: 'Back',
        knar: 'Knees',
        hofte: 'Hip',
        nyledd: 'New joint',
        artrose: 'Osteoarthritis',
        fibro: 'Fibromyalgia',
        nevro: 'Neurological/ME',
        hjerte: 'Heart',
        etterop: 'After surgery',
        ettersyk: 'After illness',
        ingen: 'None of these',
      },
    } as Record<string, Record<string, string>>,
    emptyTitle: 'No quiz sessions in this range',
    emptyHint:
      'Sessions show up here once the quiz beacon is live on the draft theme and someone opens the training calculator.',
  },
};

type Msgs = (typeof MESSAGES)['nb'];

type QuizData = Awaited<ReturnType<typeof api.quiz>>;
type SessionRow = QuizData['sessions'][number];
type StepRow = QuizData['steps'][number];
type AnswerRow = QuizData['answers'][number];

function useQuiz(from: string, to: string) {
  return useQuery({
    queryKey: ['quiz', from, to],
    queryFn: () => api.quiz({ from, to }),
  });
}

const EM_DASH = '—';
const MINUS = '−';

/** num ÷ den as a 1-decimal percent; null (→ em dash) when the denominator is 0. */
function pctOf(num: number, den: number): number | null {
  return den > 0 ? Math.round((num / den) * 1000) / 10 : null;
}

/** Fixed presentation order for the answer drop-off groups. */
const ANSWER_QID_ORDER = ['alder', 'mirror', 'goals', 'obstacle', 'body', 'doctor', 'readiness'];

function answerLabel(m: Msgs, qid: string, answerId: string): string {
  const label = m.answerLabels[qid]?.[answerId];
  if (label) return label;
  // goals ids vary → raw id capitalized; any other unknown id falls back raw.
  if (qid === 'goals') return answerId.charAt(0).toUpperCase() + answerId.slice(1);
  return answerId;
}

function stepLabel(m: Msgs, qid: string | null, step: number | null): string {
  if (qid) {
    const label = m.qidLabels[qid];
    if (label) return label;
  }
  return step === null ? EM_DASH : m.stepFallback(step);
}

function eventLabel(m: Msgs, event: string): string {
  const map: Record<string, string> = m.events;
  return map[event] ?? event;
}

// Session times are exact instants → render in the viewer's clock, nb-NO style
// (same convention as the Attribution click list).
const timeFmt = new Intl.DateTimeFormat('nb-NO', { hour: '2-digit', minute: '2-digit' });
const timeSecFmt = new Intl.DateTimeFormat('nb-NO', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/** ISO timestamp → { time: 'HH:MM', date: 'DD.MM.YY' }. */
function formatStartTime(iso: string): { time: string; date: string } {
  return { time: timeFmt.format(new Date(iso)), date: formatIsoDate(iso.slice(0, 10)).date };
}

/** Seconds → 'm:ss' (e.g. 92 → '1:32'). */
function formatDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Discovery string → [{label, value}] rows. Supports BOTH formats: the new
 *  newline-per-answer ('Alder: Y\nSituasjon: …') and the legacy ' | '-joined. */
function parseDiscovery(discovery: string): { key: string; label: string; value: string }[] {
  return discovery
    .split(/\n+|\s\|\s/)
    .filter((p) => p.trim())
    .map((part, i) => {
      const sep = part.indexOf(': ');
      return {
        key: `d-${i}-${part.slice(0, 24)}`,
        label: sep > 0 ? part.slice(0, sep) : '',
        value: sep > 0 ? part.slice(sep + 2) : part,
      };
    });
}

export function QuizPage() {
  const m = useViewMessages(MESSAGES);
  const { from, to } = useFilters();
  const query = useQuiz(from, to);

  if (query.isLoading)
    return (
      <PageFrame m={m} from={from} to={to}>
        <LoadingState />
      </PageFrame>
    );
  if (query.isError || !query.data)
    return (
      <PageFrame m={m} from={from} to={to}>
        <ErrorState onRetry={() => query.refetch()} />
      </PageFrame>
    );

  const data = query.data;

  return (
    <PageFrame m={m} from={from} to={to}>
      {data.totals.sessions === 0 ? (
        <EmptyState title={m.emptyTitle}>{m.emptyHint}</EmptyState>
      ) : (
        <>
          <KpiRow m={m} totals={data.totals} />
          <FunnelCard m={m} steps={data.steps} landed={data.totals.sessions} />
          {data.answers.length > 0 ? <AnswerDropoffCard m={m} answers={data.answers} /> : null}
          <SessionsCard m={m} rows={data.sessions} />
        </>
      )}
    </PageFrame>
  );
}

function PageFrame({
  m,
  from,
  to,
  children,
}: {
  m: Msgs;
  from: string;
  to: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-title text-fg">{m.title}</h1>
        <p className="mt-1 max-w-3xl text-body text-fg-muted">
          {m.sub(formatIsoDate(from).date, formatIsoDate(to).date)}
        </p>
      </header>
      {children}
    </div>
  );
}

/** Shared card header: one clear heading + one-line explainer. */
function CardHeading({ title, sub }: { title: string; sub: string }) {
  return (
    <div>
      <h2 className="text-section text-fg">{title}</h2>
      <p className="mt-0.5 max-w-3xl text-label text-fg-muted">{sub}</p>
    </div>
  );
}

/** Rates first (the comparison numbers vs the main LP), counts as context. */
function KpiRow({ m, totals }: { m: Msgs; totals: QuizData['totals'] }) {
  const openRate = pctOf(totals.started, totals.sessions);
  const completionRate = pctOf(totals.reachedResult, totals.started);
  const ctaRate = pctOf(totals.ctaClicked, totals.reachedResult);
  const leadRate = pctOf(totals.leadsSent, totals.started);

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      <StatCard
        label={m.statSessions}
        source="derived"
        value={formatMetric(totals.sessions, 'int')}
        sub={m.statSessionsSub}
      />
      <StatCard
        label={m.statOpenRate}
        source="derived"
        value={formatMetric(openRate, 'pct')}
        sub={m.statOpenRateSub(formatMetric(totals.started, 'int'))}
      />
      <StatCard
        label={m.statCompletionRate}
        source="derived"
        value={formatMetric(completionRate, 'pct')}
        sub={m.statCompletionRateSub(formatMetric(totals.reachedResult, 'int'))}
      />
      <StatCard
        label={m.statCtaRate}
        source="derived"
        value={formatMetric(ctaRate, 'pct')}
        sub={m.statCtaRateSub(formatMetric(totals.ctaClicked, 'int'))}
      />
      <StatCard
        label={m.statLeadRate}
        source="lead"
        value={formatMetric(leadRate, 'pct')}
        sub={m.statLeadRateSub(formatMetric(totals.leadsSent, 'int'))}
      />
    </div>
  );
}

function FunnelCard({ m, steps, landed }: { m: Msgs; steps: StepRow[]; landed: number }) {
  // Synthetic 100% baseline row, then one bar per step (already ordered 1..N).
  const rows = [
    { key: 'landed', label: m.landed, reached: landed, pct: 100 as number | null, drop: 0 },
    ...steps.map((s) => ({
      key: `step-${s.step}`,
      label: stepLabel(m, s.qid, s.step),
      reached: s.reached,
      pct: s.pctOfLanded,
      drop: s.dropFromPrev,
    })),
  ];

  return (
    <Card className="flex flex-col gap-3 p-3 sm:p-4">
      <CardHeading title={m.funnelTitle} sub={m.funnelSub} />
      <div className="flex flex-col gap-1.5">
        {rows.map((row) => {
          const widthPct = Math.max(3, row.pct ?? 0);
          return (
            <div key={row.key} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
              <div className="w-full text-left text-label text-fg-muted sm:w-36 sm:shrink-0 sm:text-right">
                {row.label}
              </div>
              <div className="relative h-7 flex-1 overflow-hidden rounded-md bg-surface-2">
                {/* pure bar fill */}
                <div className="h-full rounded-md bg-brand/70" style={{ width: `${widthPct}%` }} />
                {/* count over the track — muted, always readable */}
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-label text-fg-muted tabular">
                  {formatMetric(row.reached, 'int')}
                </span>
              </div>
              <div className="flex w-full items-center gap-2 text-left text-label tabular sm:w-32 sm:shrink-0">
                <span className="text-fg-subtle">{formatMetric(row.pct, 'pct')}</span>
                {row.drop > 0 ? (
                  <Badge tone="neutral">
                    {MINUS}
                    {formatMetric(row.drop, 'int')}
                  </Badge>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/**
 * «Hvem faller fra?» — anonymous answer tallies (quiz_answer_daily, never
 * linkable to a person). Collapsed by default so it doesn't dominate the page.
 */
function AnswerDropoffCard({ m, answers }: { m: Msgs; answers: AnswerRow[] }) {
  const groups = ANSWER_QID_ORDER.map((qid) => {
    const rows = answers
      .filter((a) => a.qid === qid)
      .map((a) => ({ ...a, dropped: Math.max(0, a.all - a.completed) }))
      .sort((a, b) => b.all - a.all);
    const totalDropped = rows.reduce((sum, r) => sum + r.dropped, 0);
    return { qid, label: m.qidLabels[qid] ?? qid, rows, totalDropped };
  }).filter((g) => g.rows.length > 0);

  if (groups.length === 0) return null;

  return (
    <Card className="p-3 sm:p-4">
      <details className="group">
        <summary className="flex cursor-pointer list-none items-start justify-between gap-3 [&::-webkit-details-marker]:hidden">
          <CardHeading title={m.answersTitle} sub={m.answersSub} />
          <ChevronDown
            className="mt-1 size-4 shrink-0 text-fg-subtle transition-transform group-open:rotate-180"
            aria-hidden
          />
        </summary>
        <div className="mt-4 flex flex-col gap-5">
          {groups.map((g) => (
            <div key={g.qid} className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-3">
                <span className="text-label font-semibold text-fg">{g.label}</span>
                <span className="flex shrink-0 text-micro text-fg-subtle">
                  <span className="w-14 text-right">{m.colAll}</span>
                  <span className="w-14 text-right">{m.colCompleted}</span>
                  <span className="w-14 text-right">{m.colDropped}</span>
                </span>
              </div>
              {g.rows.map((r) => {
                const label = answerLabel(m, g.qid, r.answerId);
                const sharePct = g.totalDropped > 0 ? (r.dropped / g.totalDropped) * 100 : 0;
                return (
                  <div
                    key={r.answerId}
                    className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3"
                  >
                    <span
                      className="truncate text-label text-fg-muted sm:w-48 sm:shrink-0"
                      title={label}
                    >
                      {label}
                    </span>
                    <div className="flex flex-1 items-center gap-3">
                      {/* share of this question's total drop-off — dominant quitter answer at a glance */}
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-2">
                        <div
                          className="h-full rounded-full bg-warn/80"
                          style={{ width: `${sharePct}%` }}
                        />
                      </div>
                      <span className="flex shrink-0 text-label tabular">
                        <span className="w-14 text-right text-fg-muted">
                          {formatMetric(r.all, 'int')}
                        </span>
                        <span className="w-14 text-right text-fg-muted">
                          {formatMetric(r.completed, 'int')}
                        </span>
                        <span className="w-14 text-right font-medium text-fg">
                          {formatMetric(r.dropped, 'int')}
                        </span>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </details>
    </Card>
  );
}

function DeviceCell({ m, device }: { m: Msgs; device: SessionRow['device'] }) {
  if (device === 'mobile') return <span title={m.deviceMobile}>📱</span>;
  if (device === 'desktop') return <span title={m.deviceDesktop}>💻</span>;
  return (
    <span className="text-fg-subtle" title={m.deviceUnknown}>
      {EM_DASH}
    </span>
  );
}

/** ONE status per session: Lead ▸ CTA ▸ Resultat ▸ muted «Falt av på {steg}». */
function StatusCell({ m, row }: { m: Msgs; row: SessionRow }) {
  const badge = row.leadSent ? (
    <Badge tone="good">{m.statusLead}</Badge>
  ) : row.ctaClicked ? (
    <Badge tone="booking">{m.statusCta}</Badge>
  ) : row.maxQid === 'result' ? (
    <Badge tone="neutral">{m.statusResult}</Badge>
  ) : (
    <span className="text-label text-fg-subtle">
      {m.statusFell(stepLabel(m, row.maxQid, row.maxStep))}
    </span>
  );

  return (
    <span className="inline-flex items-center gap-1.5">
      {badge}
      {row.callbackRequested ? (
        <span
          role="img"
          title={m.callbackTitle}
          aria-label={m.callbackTitle}
          className="text-label"
        >
          📞
        </span>
      ) : null}
      {row.hasFbclid ? (
        <span
          role="img"
          className="size-1.5 shrink-0 rounded-full bg-accent-click"
          title={m.adDotTitle}
          aria-label={m.adDotTitle}
        />
      ) : null}
    </span>
  );
}

function SessionsCard({ m, rows }: { m: Msgs; rows: SessionRow[] }) {
  return (
    <Card className="overflow-hidden p-0">
      <div className="p-3 sm:p-4">
        <CardHeading title={m.sessionsTitle} sub={m.sessionsSub} />
      </div>
      <div className="overflow-x-auto border-t border-border">
        <table className="w-full min-w-[34rem] border-collapse text-body">
          <thead>
            <tr className="border-b border-border bg-surface-2">
              <th
                scope="col"
                className="whitespace-nowrap px-3 py-2 text-left text-label font-medium text-fg-muted"
              >
                {m.colTime}
              </th>
              <th scope="col" className="px-3 py-2 text-left text-label font-medium text-fg-muted">
                {m.colDevice}
              </th>
              <th
                scope="col"
                className="whitespace-nowrap px-3 py-2 text-left text-label font-medium text-fg-muted"
              >
                {m.colReached}
              </th>
              <th
                scope="col"
                className="whitespace-nowrap px-3 py-2 text-left text-label font-medium text-fg-muted"
              >
                {m.colStatus}
              </th>
              <th scope="col" className="w-10 px-3 py-2">
                <span className="sr-only">{m.expandRow}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <SessionRowView key={row.sessionId} m={m} row={row} />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function SessionRowView({ m, row }: { m: Msgs; row: SessionRow }) {
  const [open, setOpen] = useState(false);
  const t = formatStartTime(row.startedAt);

  // Pre-build journey items with stable keys (chronological; timestamps can repeat).
  const journey = row.journey.map((ev, i) => ({
    key: `${row.sessionId}-ev-${i}`,
    time: timeSecFmt.format(new Date(ev.at)),
    event: eventLabel(m, ev.event),
    step: ev.step !== null || ev.qid !== null ? stepLabel(m, ev.qid, ev.step) : null,
  }));

  return (
    <Fragment>
      <tr className="group border-b border-border/60 transition-fast hover:bg-surface-2/50">
        <td className="whitespace-nowrap px-3 py-2 tabular">
          <span className="text-fg">{t.time}</span>
          <span className="ml-2 text-fg-subtle">{t.date}</span>
        </td>
        <td className="px-3 py-2">
          <DeviceCell m={m} device={row.device} />
        </td>
        <td className="whitespace-nowrap px-3 py-2">
          <span className="text-fg">{stepLabel(m, row.maxQid, row.maxStep)}</span>
          <span className="ml-1.5 text-fg-subtle">
            · {m.stepWord} {row.maxStep}
          </span>
        </td>
        <td className="whitespace-nowrap px-3 py-2">
          <StatusCell m={m} row={row} />
        </td>
        <td className="px-3 py-2 text-right">
          <button
            type="button"
            aria-expanded={open}
            aria-label={m.expandRow}
            onClick={() => setOpen((o) => !o)}
            className="flex size-7 items-center justify-center rounded-md text-fg-subtle transition-fast hover:bg-surface-2 hover:text-fg"
          >
            {open ? (
              <ChevronDown className="size-4" aria-hidden />
            ) : (
              <ChevronRight className="size-4" aria-hidden />
            )}
          </button>
        </td>
      </tr>
      {open ? (
        <tr className="border-b border-border/60 bg-surface-2/30">
          <td colSpan={5} className="px-3 pb-4 pt-2 sm:px-4">
            <div className={cn('grid gap-3', row.lead && 'sm:grid-cols-2')}>
              <div className="rounded-lg border border-border bg-surface p-3">
                <span className="mb-2 block text-micro font-semibold uppercase tracking-wider text-fg-muted">
                  {m.journeyTitle} · {formatDuration(row.durationSec)}
                </span>
                <ol className="flex flex-col gap-1">
                  {journey.map((ev) => (
                    <li key={ev.key} className="flex items-baseline gap-3 text-label">
                      <span className="w-16 shrink-0 tabular text-fg-subtle">{ev.time}</span>
                      <span className="w-20 shrink-0 text-fg-muted">{ev.event}</span>
                      <span className="text-fg">{ev.step ?? ''}</span>
                    </li>
                  ))}
                </ol>
                {row.ad.adName ? (
                  <p className="mt-2 truncate border-t border-border pt-2 text-label text-fg-subtle">
                    {m.adDotTitle}: <span className="text-fg-muted">{row.ad.adName}</span>
                  </p>
                ) : null}
              </div>
              {row.lead ? <PersonPanel m={m} lead={row.lead} /> : null}
            </div>
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
}

/** The owner's market intelligence: who the converted session actually is. */
function PersonPanel({ m, lead }: { m: Msgs; lead: NonNullable<SessionRow['lead']> }) {
  const discovery = lead.discovery ? parseDiscovery(lead.discovery) : [];

  return (
    <div className="rounded-lg border border-border bg-surface p-3">
      <span className="mb-2 block text-micro font-semibold uppercase tracking-wider text-fg-muted">
        {m.personTitle}
      </span>
      <div className="text-body font-semibold text-fg">{lead.name}</div>
      {lead.phone ? <div className="mt-0.5 text-body text-fg tabular">{lead.phone}</div> : null}
      <div className={cn('mt-1.5 text-label', lead.forwarded ? 'text-good-fg' : 'text-warn-fg')}>
        {lead.forwarded ? m.personInMomence : m.personNotForwarded}
      </div>
      {discovery.length > 0 ? (
        <dl className="mt-2.5 flex flex-col gap-1 border-t border-border pt-2.5">
          {discovery.map((it) => (
            <div key={it.key} className="flex gap-2 text-label">
              <dt className="w-24 shrink-0 text-fg-subtle">{it.label}</dt>
              <dd className="min-w-0 flex-1 text-fg">{it.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
}
